import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  deletePost,
  getAdminPost,
  publishPost,
  unpublishPost,
} from '../api/posts';
import DeleteButton from '../components/DeleteButton';
import FormattedDate from '../components/FormattedDate';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import Card from '../components/common/Card';
import StatusMessage from '../components/StatusMessage';

const PostDetailPage: React.FC = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const postQuery = useQuery({
    queryKey: ['admin', 'post', id, token],
    queryFn: () => getAdminPost(id!, token!),
    enabled: Boolean(id && token) && user?.user_role === 'admin',
  });

  const publishMutation = useMutation({
    mutationFn: () => publishPost(id!, token!),
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(['admin', 'post', id, token], updatedPost);
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'detail', id] });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishPost(id!, token!),
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(['admin', 'post', id, token], updatedPost);
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'detail', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePost(id!, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.removeQueries({ queryKey: ['posts', 'detail', id] });
      navigate('/admin/posts');
    },
  });

  if (!user || user.user_role !== 'admin') {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <Card className="text-center">
            <h2 className="font-serif text-3xl text-[#2c201b]">Restricted draft.</h2>
            <p className="mt-3 text-[#5b5048]">You must be an admin to view post details.</p>
          </Card>
        </section>
      </div>
    );
  }

  if (postQuery.isLoading) {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="px-4 py-16 text-center">
          <StatusMessage type="loading" className="mx-auto max-w-sm" />
        </section>
      </div>
    );
  }

  if (postQuery.error && !postQuery.data) {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <StatusMessage
            type="error"
            message={postQuery.error instanceof Error ? postQuery.error.message : 'Failed to fetch post.'}
          />
        </section>
      </div>
    );
  }

  const post = postQuery.data;
  if (!post) {
    return null;
  }

  const errorMessage =
    (publishMutation.error instanceof Error && publishMutation.error.message) ||
    (unpublishMutation.error instanceof Error && unpublishMutation.error.message) ||
    (deleteMutation.error instanceof Error && deleteMutation.error.message) ||
    '';
  const isPublished = post.status === 'published' || !!post.published_at;
  const actionLoading =
    publishMutation.isPending || unpublishMutation.isPending || deleteMutation.isPending;

  return (
    <BookPage
      eyebrow="Admin"
      title={post.title}
      intro="Inspect the final copy, confirm publishing status, and keep the post record tidy."
      bodyClassName="space-y-6"
      maxWidthClassName="max-w-6xl"
    >
      {errorMessage && <div className="rounded-[0.8rem] border border-red-200 bg-red-50/90 p-3 text-sm text-red-700">{errorMessage}</div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
          <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Status</dt>
          <dd className="mt-2 text-[color:var(--wf-ink)]">{post.status}</dd>
        </div>
        <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
          <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Created</dt>
          <dd className="mt-2 text-[color:var(--wf-ink)]"><FormattedDate date={post.created_at} /></dd>
        </div>
        <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
          <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Updated</dt>
          <dd className="mt-2 text-[color:var(--wf-ink)]"><FormattedDate date={post.updated_at} /></dd>
        </div>
        <div className="border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4">
          <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Published</dt>
          <dd className="mt-2 text-[color:var(--wf-ink)]"><FormattedDate date={post.published_at} /></dd>
        </div>
      </div>

      <div className="border-y border-[color:var(--wf-border)] py-6">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Slug</p>
          <p className="mt-2 break-all font-mono text-sm text-[color:var(--wf-ink)]">{post.slug}</p>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Excerpt</p>
        <p className="mt-3 border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-4 text-sm leading-7 text-[color:var(--wf-ink)]">
          {post.excerpt}
        </p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Content</p>
        <pre className="mt-3 whitespace-pre-wrap border border-[color:var(--wf-border)] bg-[rgba(255,255,255,0.18)] p-5 text-sm leading-7 text-[color:var(--wf-ink)]">
          {post.content}
        </pre>
      </div>

      <div className="flex flex-wrap gap-3 border-t border-[#8f7e69]/15 pt-6">
        {isPublished ? (
          <Button type="button" variant="secondary" onClick={() => unpublishMutation.mutate()} isLoading={unpublishMutation.isPending}>
            Unpublish
          </Button>
        ) : (
          <Button type="button" onClick={() => publishMutation.mutate()} isLoading={publishMutation.isPending}>
            Publish
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={() => navigate(`/blog/${post.id}`)}>
          View public post
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate('/admin/posts')}>
          Back to posts
        </Button>
        <DeleteButton
          onDelete={() => deleteMutation.mutate()}
          loading={deleteMutation.isPending || actionLoading}
          confirmMessage="Are you sure you want to delete this post? This action cannot be undone."
        >
          Delete post
        </DeleteButton>
      </div>
    </BookPage>
  );
};

export default PostDetailPage;
